import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Pagination } from '@mui/material'
import { FormattedMessage } from 'react-intl'

import { Preloader } from '../../../../components/UI'
import { MONTHS_LIST } from '../../constants'
import { ReusableFilters } from '../ReusableFilters'
import TournamentItem from '../../../../components/list-items/components/tournamentItem'

import { isAuthenticated } from '../../../../helpers'
import { useGetTournament } from './hooks/useGetTournament'
import { useGetAuthTournament } from './hooks/useGetAuthTournament'
import { useGetGames } from './hooks/useGetGames'
import { PAGINATION_STYLES } from './config'

import styles from './styles.module.scss'

export const TournamentsList = () => {
  const [isAuthUser] = useState(isAuthenticated())
  const [isLoading, setIsLoading] = useState(false)
  const [isFilterLoading, setIsFilterLoading] = useState(false)
  const [tournamentList, setTournamentList] = useState()
  const [pageTournament, setPageTournament] = useState(1)
  const [defaultGame, setDefaultGame] = useState(null)
  const [defaultMonth, setDefaultMonth] = useState(null)
  const [games, setGames] = useState([])
  const [isGamesLoading, setIsGamesLoading] = useState(false)

  const tournaments = useMemo(
    () => tournamentList?.tournaments || [],
    [tournamentList?.tournaments]
  )

  const defaultValues = useMemo(
    () => ({
      game: defaultGame,
      month: defaultMonth,
    }),
    [defaultGame, defaultMonth]
  )

  const filterConfig = useMemo(
    () => [
      {
        key: 'game',
        label: 'createEvent.title.titleGames.placeholder',
        options: games || [],
        placeholder: 'Select game',
      },
      {
        key: 'month',
        label: 'calendar.filter.selectMonth',
        options: MONTHS_LIST,
        placeholder: 'Select month',
      },
    ],
    [games]
  )

  const tournamentItems = useMemo(() => {
    return tournaments.map(items => (
      <li key={items.id}>
        <TournamentItem {...items} />
      </li>
    ))
  }, [tournaments])

  const hasTournaments = useMemo(
    () => tournaments.length > 0,
    [tournaments.length]
  )

  const handleChangeTournaments = useCallback((_, value) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setPageTournament(value)
  }, [])

  const paginationProps = useMemo(
    () => ({
      count: tournamentList?.paginator?.lastPage || 0,
      page: tournamentList?.paginator?.currentPage ?? 1,
      onChange: handleChangeTournaments,
      variant: 'outlined',
      sx: PAGINATION_STYLES,
    }),
    [
      tournamentList?.paginator?.lastPage,
      tournamentList?.paginator?.currentPage,
      handleChangeTournaments,
    ]
  )

  const { getTournament } = useGetTournament({
    pageTournament,
    defaultGame,
    defaultMonth,
    setTournamentList,
    setIsLoading,
    setIsFilterLoading,
  })

  const { getAuthTournament } = useGetAuthTournament({
    pageTournament,
    defaultGame,
    defaultMonth,
    setTournamentList,
    setIsLoading,
    setIsFilterLoading,
  })

  const { getGames } = useGetGames({
    setGames,
    setIsLoading: setIsGamesLoading,
  })

  const handleFiltersChange = useCallback(({ game, month }) => {
    setDefaultGame(game)
    setDefaultMonth(month)
  }, [])

  useEffect(() => {
    getGames()
  }, [getGames])

  useEffect(() => {
    const isFilterChange = !!(defaultGame || defaultMonth)

    if (isAuthUser) {
      return getAuthTournament(isFilterChange)
    }

    return getTournament(isFilterChange)
  }, [
    isAuthUser,
    defaultGame,
    defaultMonth,
    pageTournament,
    getAuthTournament,
    getTournament,
  ])

  if (isLoading && !tournamentList) {
    return <Preloader />
  }

  return (
    <section className={styles.calendarList} data-testid='tournament-list'>
      <div className={styles.layoutContainer}>
        <ReusableFilters
          filterConfigs={filterConfig}
          defaultValues={defaultValues}
          onFiltersChange={handleFiltersChange}
          isLoading={isGamesLoading}
          title='filters'
        />

        <div className={styles.content}>
          {hasTournaments ? (
            <div className={styles.tournamentContainer}>
              {isFilterLoading && (
                <div className={styles.filterLoaderOverlay}>
                  <Preloader />
                </div>
              )}

              <ul
                className={styles.tournamentGrid}
                data-testid='tournament-grid'
              >
                {tournamentItems}
              </ul>
            </div>
          ) : (
            <div className={styles.emptyData} data-testid='empty-state'>
              <FormattedMessage id='calendar.noTournaments' />
            </div>
          )}

          {hasTournaments && (
            <div className={styles.paginatorWrapper} data-testid='pagination'>
              <Pagination {...paginationProps} />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default TournamentsList
